import { uniq } from 'lodash';

/**
 * Single source of truth for protein vocabulary: the animal synonyms, the words that void a match,
 * and the cut and qualifier vocabulary the claim rules read.
 *
 * Data only, in the shape `common/FlaggableIngredients` uses. The matching logic lives in shef-api:
 * `ingredientMatching.ts` compiles the synonym and not-lists into segment regexes, and
 * `utils/proteinClaims.ts` holds the tokenizer and the claim rules that consult the cut vocabulary.
 */

export const BEEF_SYNONYMS = [
  'beef',
  'steak',
  'brisket',
  'cow',
  'tri-tip',
  'sirloin',
  'ribeye',
  'rib-eye',
  'filet mignon',
  'oxtail',
  'chuck',
  'burger',
  'hamburger',
  'cheeseburger',
  'short rib',
  'prime rib',
  'rib roast',
  'veal',
  'bison',
  'tripe',
  'keema',
  'kheema',
  'wagyu',
  'wagu',
];

export const PORK_SYNONYMS = [
  'pork',
  'bacon',
  'pancetta',
  'pig',
  'ham',
  'ribs',
  'spare ribs',
  'prosciutto',
  'sausage',
  'andouille',
  'chorizo',
  'kielbasa',
  'pepperoni',
  'salami',
  'spam',
  'hot dog',
  'hotdog',
  'trotters',
  'linguica',
];

export const CHICKEN_SYNONYMS = ['chicken', 'duck', 'turkey', 'hen', 'gizzard', 'poultry'];

export const LAMB_SYNONYMS = ['lamb', 'goat', 'mutton'];

export const FISH_SYNONYMS = [
  'fish',
  'salmon',
  'tuna',
  'anchovy',
  'anchovies',
  'basa',
  'bass',
  'bhetki',
  'bonito',
  'branzino',
  'catfish',
  'cod',
  'eel',
  'fishcake',
  'flounder',
  'haddock',
  'halibut',
  'hake',
  'herring',
  'hilsa',
  'ilish',
  'katla',
  'kingfish',
  'kipper',
  'lox',
  'mackerel',
  'mahi mahi',
  'milkfish',
  'mullet',
  'perch',
  'pollock',
  'pomfret',
  'rohu',
  'saltfish',
  'sardine',
  'snapper',
  'swai',
  'swordfish',
  'tilapia',
  'trout',
  'unagi',
  'whitefish',
];

/**
 * On its own the word names fish as readily as shellfish. The ingredient tagger reads it as
 * shellfish; a dish name that uses it is satisfied by either fish or shellfish ingredients.
 */
export const GENERIC_SEAFOOD_TERM = 'seafood';

export const SHELLFISH_PROTEIN_SYNONYMS = [
  'lobster',
  'mussel',
  'octopus',
  'oyster',
  'prawn',
  'scallop',
  'scampi',
  'shellfish',
  'shrimp',
  'squid',
  GENERIC_SEAFOOD_TERM,
  'abalone',
  'calamari',
  'clam',
  'crab',
  'crawfish',
  'crayfish',
  'krill',
];

export const PROTEIN_SYNONYM_MAP = {
  beef: BEEF_SYNONYMS,
  pork: PORK_SYNONYMS,
  chicken: CHICKEN_SYNONYMS,
  lamb: LAMB_SYNONYMS,
  fish: FISH_SYNONYMS,
  shellfish: SHELLFISH_PROTEIN_SYNONYMS,
};

export const EGG_PROTEIN_SYNONYMS = ['egg', 'omelette', 'omelet', 'frittata', 'anda'];
export const EGG_PROTEIN_EXCLUSIONS = [
  'eggplant',
  'egg plant',
  'eggless',
  'egg free',
  'egg-free',
  'wash',
  'noodle',
  'pasta',
  'brioche',
  'vegan',
];

export const TOFU_PROTEIN_SYNONYMS = [
  'tofu',
  'bean curd',
  'tempeh',
  'seitan',
  'edamame',
  'soya chunks',
  'soy chunks',
  'soy protein',
];

export const BEANS_LENTILS_PROTEIN_SYNONYMS = [
  'lentil',
  'dal',
  'daal',
  'dhal',
  'chickpea',
  'chana',
  'channa',
  'garbanzo',
  'rajma',
  'moong',
  'mung',
  'masoor',
  'toor',
  'urad',
  'lobia',
  'beans',
  'fava',
  'black eyed peas',
  'hummus',
  'falafel',
];
export const BEANS_LENTILS_PROTEIN_EXCLUSIONS = [
  'flour',
  'green bean',
  'string bean',
  'french bean',
  'long bean',
  'vanilla bean',
  'coffee bean',
  'sprout',
  'sauce',
];

export const DAIRY_PROTEIN_SYNONYMS = [
  'paneer',
  'cottage cheese',
  'cheese',
  'yogurt',
  'yoghurt',
  'curd',
  'dahi',
  'milk',
  'labneh',
  'kefir',
  'ricotta',
  'mozzarella',
  'feta',
  'burrata',
  'halloumi',
  'skyr',
  'whey',
];
export const DAIRY_PROTEIN_EXCLUSIONS = [
  'bean curd',
  'coconut',
  'almond',
  'oat',
  'soy',
  'soya',
  'cashew',
  'rice milk',
  'condensed',
  'vegan',
  'dairy free',
  'dairy-free',
  'non-dairy',
  'non dairy',
];

export const NUT_PROTEIN_SYNONYMS = [
  'cashew',
  'almond',
  'peanut',
  'groundnut',
  'walnut',
  'pistachio',
  'pecan',
  'hazelnut',
  'macadamia',
];
export const GRAIN_PROTEIN_SYNONYMS = [
  'quinoa',
  'oats',
  'oatmeal',
  'farro',
  'barley',
  'bulgur',
  'millet',
  'buckwheat',
  'freekeh',
  'teff',
  'amaranth',
  'sorghum',
];
export const NUTS_GRAINS_PROTEIN_EXCLUSIONS = ['oil', 'milk', 'extract', 'essence', 'flour'];

/** No grains, and no tofu: tofu is its own intake card next to plant protein. */
export const PLANT_PROTEIN_SYNONYMS = [...BEANS_LENTILS_PROTEIN_SYNONYMS, ...NUT_PROTEIN_SYNONYMS, 'tempeh', 'seitan'];
export const PLANT_PROTEIN_EXCLUSIONS = uniq([...BEANS_LENTILS_PROTEIN_EXCLUSIONS, ...NUTS_GRAINS_PROTEIN_EXCLUSIONS]);

export const PROTEIN_FLAVOR_FORMS = ['seasoning', 'sauce', 'powder', 'gravy', 'masala', 'dressing', 'spice'];

export const VEGAN_MARKERS = ['plant-based', 'plant based', 'plantbased', 'vegan'];

/**
 * Brands and phrasings that declare a whole dish meatless, whichever animal it is named after.
 */
export const IMITATION_MARKERS = [
  ...VEGAN_MARKERS,
  'beyond',
  'impossible',
  'gardein',
  'mock',
  'meatless',
  'meat free',
  'faux',
  'imitation',
  'immitation',
];

/**
 * `<animal>less` spellings, per animal so "beefless chili with real bacon" keeps its bacon.
 * These forms are common in our real-world data.
 */
const addLessSuffix = (animal: string): string[] => [`${animal}less`, `${animal}-less`, `${animal} less`];

export const BASE_NOT_BEEF_LIST = [...addLessSuffix('beef'), 'cow pea', 'cowpea', 'beef base'];
export const BASE_NOT_CHICKEN_LIST = [
  ...addLessSuffix('chicken'),
  'hen of the woods',
  'poultry seasoning',
  'chicken base',
  'chicken powder',
];
export const BASE_NOT_PORK_LIST = addLessSuffix('pork');

export const BASE_NOT_FISH_LIST = ['fish sauce', 'fish masala', 'swedish fish', 'anchovy paste'];

export const NOT_PORK_RIB_PHRASES = ['rib eye', 'rib roast', 'rib steak'];

/**
 * Words that void a whole comma segment, for cases where nothing in the segment is a portion.
 *
 * Meat substitutes (`tofu`, `seitan`, `mushroom`, …) are deliberately absent: they rename only the
 * word they precede, via `PLANT_QUALIFIERS` and `IMITATION_QUALIFIERS`, because a segment-level
 * reject loses the real animal standing next to them in "chicken and mushroom pie".
 */
export const NOT_PROTEIN_PORTION_LIST = [
  ...IMITATION_MARKERS,
  'soy',
  'soya',
  'broth',
  'stock',
  'fat',
  'bone marrow',
  'bouilan',
  'bouillion',
  'bouillon',
  'boulion',
  'boullion',
  'bullion',
  'bouilluon',
  'boulian',
  'buillion',
  'soup base',
  'soup cube',
  'casing',
  'consomme',
  'drippings',
  'gelatin',
  'lard',
  'marrowbone',
];

export const NOT_PROTEIN_LIST = [...NOT_PROTEIN_PORTION_LIST, ...PROTEIN_FLAVOR_FORMS];

/**
 * Shellfish-derived but not a shellfish portion, so `FishAndSeafoodSauces` reports these instead.
 *
 * Contrast `NOT_SHELLFISH_LIST`, whose entries contain no shellfish at all and so are excluded from
 * prose as well.
 */
export const NOT_SHELLFISH_PORTION_LIST = ['shrimp paste', 'oyster sauce'];

/**
 * Only a cut can be reassigned by a neighbouring vegetable or rival animal: "cauliflower steak" is
 * not beef, "cabbage and beef" is. `IMITATION_QUALIFIERS` are not bound by this.
 */
export const CUT_TERMS = [
  'steak',
  'brisket',
  'tri tip',
  'sirloin',
  'ribeye',
  'rib eye',
  'filet mignon',
  'oxtail',
  'chuck',
  'short rib',
  'bacon',
  'pancetta',
  'ham',
  'ribs',
  'spare ribs',
  'prosciutto',
  'burger',
  'hamburger',
  'cheeseburger',
  'sausage',
  'andouille',
  'chorizo',
  'pepperoni',
  'salami',
  'hot dog',
  'hotdog',
  'keema',
  'kheema',
];

/**
 * Plants that share a cut word with an animal: "cauliflower steak", "watermelon ham", "tofu steak".
 *
 * These claim only a cut standing after them in the same phrase, never a plain animal name, because
 * they are eaten alongside meat at least as often as instead of it — "Tofu & Chicken Stir-Fry" and
 * "Green Jackfruit & Shrimp Curry" both have the animal in them. A word that reliably renames the
 * animal it precedes goes in `IMITATION_QUALIFIERS` instead; almost nothing qualifies.
 *
 * Membership test: "<plant> <cut>" has to mean the plant IS the cut. `potato` fails it — "potato
 * bacon soup" is real pork — and `avocado` fails it on "avocado burger", so neither is here.
 */
export const PLANT_QUALIFIERS = [
  'cauliflower',
  'mushroom',
  'portobello',
  'jackfruit',
  'tofu',
  'tempeh',
  'tempe',
  'seitan',
  'bean',
  'chickpea',
  'lentil',
  'quinoa',
  'eggplant',
  'aubergine',
  'cabbage',
  'celeriac',
  'watermelon',
  'banana',
  'beet',
  'beetroot',
  'kohlrabi',
  'squash',
  'pumpkin',
  'zucchini',
  'carrot',
  'broccoli',
  'garbanzo',
  'falafel',
  'soya',
];

/**
 * Meatless substances that constitute a cut without being plants: "paneer burger", "paneer keema".
 *
 * Kept apart from `PLANT_QUALIFIERS` because neither is a plant, and out of the whole-dish imitation
 * markers because a dish can hold both these and real meat. `soy` is deliberately absent from both:
 * in a dish name it is nearly always the sauce, so it would take the beef out of "soy glazed steak".
 */
export const MEATLESS_CUT_QUALIFIERS = ['paneer'];

export const CUT_QUALIFIERS = [...PLANT_QUALIFIERS, ...MEATLESS_CUT_QUALIFIERS];

/**
 * Category words that rename whatever animal word they precede: "veggie burger", "vegetarian beef".
 *
 * Unlike `PLANT_QUALIFIERS` these are not limited to cuts, because they name no ingredient of their
 * own — nothing is being served beside the animal, so the animal word is the substitute's name.
 * Deliberately only these three: a specific plant like `tofu` or `jackfruit` fails the test, since
 * it is a real ingredient that appears next to real meat.
 *
 * Scoped to the word they precede rather than the whole text, so "garden burger with beef crumbles"
 * still reports the crumbles.
 */
export const IMITATION_QUALIFIERS = ['veggie', 'vegetarian', 'garden'];

/**
 * Preparation words a qualifier reaches across to the animal it renames: "veggie ground beef".
 *
 * Only these are skipped, so the reach stops at anything else. "garden burger with beef crumbles"
 * still reports the crumbles because `with` is not a preparation.
 */
export const PREPARATION_WORDS = [
  'ground',
  'pulled',
  'shredded',
  'minced',
  'chopped',
  'diced',
  'cubed',
  'sliced',
  'crumbled',
  'grilled',
  'roasted',
  'smoked',
  'fried',
  'braised',
  'seared',
  'cured',
  'marinated',
  'seasoned',
  'shaved',
  'flaked',
];

/**
 * Words that end the noun phrase a cut belongs to, so no qualifier reaches back across them.
 *
 * "Turkey Salisbury Steak" and "Chicken & Tofu Hamburg Steak" each name a single item, so the bird
 * and the bean curd own the cut however many words sit in between. "Veggie Burger and Bacon Platter"
 * names two items, and the bacon in the second one is real.
 */
export const PHRASE_BREAK_WORDS = ['and', 'or', 'with', 'without', 'plus', 'in', 'on', 'over', 'served', 'topped'];

/**
 * Phrases where a cut word names a flavor, a tool, or a vegetable cut to its shape.
 *
 * Enumerated rather than derived from a `cut × suffix` cross-product, because no rule separates
 * `steak fries` (potatoes) from `bacon fries` (real pork) or `steak frites` (real beef).
 */
export const CUT_PHRASE_EXCLUSIONS = [
  'corn ribs',
  'steak house',
  'steak knife',
  'steak sauce',
  'steak seasoning',
  'steak spice',
  'steak rub',
  'steak marinade',
  'steak fries',
  'steak night',
  'chuck wagon',
  'burger bun',
  'hamburger bun',
  'cheeseburger bun',
  'hot dog bun',
  'hotdog bun',
  'burger bread',
  'hamburger bread',
  'cheeseburger bread',
  'burger roll',
  'hamburger roll',
  'burger sauce',
  'burger seasoning',
  'burger night',
  'hamburger sauce',
  'hamburger seasoning',
  'cheeseburger seasoning',
  'brisket rub',
];

/**
 * Foods named after an animal that contain none of its meat.
 *
 * The contrast is with `oyster sauce`, `fish sauce`, and `worcestershire sauce`, which really are
 * flesh-derived: those belong in the byproduct matchers, which report them, rather than here. Dairy
 * counts as meatless for this purpose — `goat cheese` is goat milk, and the `Dairy` disqualifier is
 * what has an opinion about it.
 */
export const MEATLESS_ANIMAL_PHRASES = [
  'duck sauce',
  'plum duck sauce',
  'eel sauce',
  'lobster sauce',
  'chicken salt',
  'goat cheese',
  "goat's cheese",
  'goats cheese',
  'goat milk',
  "goat's milk",
  'goats milk',
  'goat yogurt',
  "goat's yogurt",
  'goats yogurt',
];
